import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import get from 'lodash.get';
import { trackSellerDetailsView } from '../tracking/pdpTracking';

// components
import { ComponentLoader } from '../../global/ComponentLoader/ComponentLoader';
import { Visibility } from '../../components/global/Visibility';
import { PdpRespDealerDetailsStats } from '../PdpSharedDealerDetailsStats/PdpRespDealerDetailsStats';
import { PdpSharedCallDealerCta } from '../PdpSharedCallDealerCta/PdpSharedCallDealerCta';
import { ContactDealerInlineFormLoader } from 'dibs-buyer-layout/exports/ContactDealerInlineFormLoader';

import {
    MAKE_OFFER,
    CONTACT_SELLER,
} from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsContants';
import { PdpSharedSellerTabsVariantLazy } from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsVariantLazy';

import { messages } from '../messages/pdpDealerDetailsMessages';

const feedbackImportFunc = () => {
    return import(
        /* webpackChunkName: "PdpRespDealerDetailsFeedback" */
        '../PdpRespDealerDetailsFeedback/PdpRespDealerDetailsFeedback'
    ).then(mod => mod.PdpRespDealerDetailsFeedback);
};

// styles
import styles from './PdpRespDealerDetails.scss';

class PdpRespDealerDetailsComponent extends React.Component {
    constructor() {
        super();
        this.state = {
            loadReviews: false,
            loadContactForm: false,
            loadEmbeddedMakeOffer: false,
        };
    }

    componentDidMount() {
        const { isEmbeddedMakeOfferTestDisabled } = get(this.props, 'viewer');
        const isNegotiable =
            get(this.props, 'item.dealerDetailsDisplayInfo.ctas.showMakeOffer') || false;
        const showEmbeddedOffer = isNegotiable && !isEmbeddedMakeOfferTestDisabled;

        this.setState({
            loadReviews: true,
            loadContactForm: !showEmbeddedOffer,
            loadEmbeddedMakeOffer: showEmbeddedOffer,
        });
    }

    render() {
        const { loadEmbeddedMakeOffer, loadContactForm } = this.state;
        const { item, user, intl, viewer, currency } = this.props;
        const { isMessageCenterEnabled, isReviewsEnabled } = viewer;
        const dealerHasPhoneNumber = !!get(item, 'seller.trackedPhone.phoneNumber');
        const inputProps = {
            hasAnimatedPlaceholder: true,
            placeholder: intl.formatMessage(messages.namePlaceholder),
        };
        const buttonProps = {
            size: 'medium',
        };

        const reviews = get(item, 'seller.orderFeedbackPdp') || null;

        return (
            <div className={styles.wrapper}>
                <Visibility onContentVisible={trackSellerDetailsView} />
                <div className={styles.sectionDetails} data-tn="pdp-dealer-info">
                    <PdpRespDealerDetailsStats
                        headerClass={styles.header}
                        seller={item.seller}
                        user={user}
                        viewer={viewer}
                        reviews={reviews}
                        showDescriptions
                    />
                </div>
                {isMessageCenterEnabled && (
                    <div className={styles.sectionForm} data-tn="pdp-contact-dealer-info">
                        {loadEmbeddedMakeOffer ? (
                            <React.Suspense fallback={''}>
                                <PdpSharedSellerTabsVariantLazy
                                    viewer={viewer}
                                    item={item}
                                    user={user}
                                    buttonProps={buttonProps}
                                    currency={currency}
                                    tabOrder={
                                        get(user, 'isVerifiedTrade')
                                            ? [CONTACT_SELLER, MAKE_OFFER]
                                            : [MAKE_OFFER, CONTACT_SELLER]
                                    }
                                />
                            </React.Suspense>
                        ) : (
                            <ContactDealerInlineFormLoader
                                viewer={viewer}
                                load={loadContactForm}
                                item={item}
                                user={user}
                                inputProps={inputProps}
                            />
                        )}
                        {loadContactForm && dealerHasPhoneNumber && (
                            <div className={styles.contactDealer}>
                                <span className={styles.contactDealerLabel}>
                                    <FormattedMessage
                                        id="PdpSharedDealerDetailsForm.call_seller"
                                        defaultMessage="You may also contact the seller by phone"
                                    />
                                </span>
                                <PdpSharedCallDealerCta item={item} user={user} />
                            </div>
                        )}
                    </div>
                )}
                {isReviewsEnabled && (
                    <ComponentLoader
                        reviews={reviews}
                        seller={item.seller}
                        importFunc={feedbackImportFunc}
                        load={this.state.loadReviews}
                        useLoadingComponent={false}
                    />
                )}
            </div>
        );
    }
}

PdpRespDealerDetailsComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    intl: intlShape.isRequired,
    currency: PropTypes.string,
};

export const PdpRespDealerDetails = createFragmentContainer(
    injectIntl(PdpRespDealerDetailsComponent),
    {
        viewer: graphql`
            fragment PdpRespDealerDetails_viewer on Viewer {
                isMessageCenterEnabled: isEnabledByLocale(featureName: "MESSAGE_CENTER")
                isReviewsEnabled: isEnabledByLocale(featureName: "ORDER_FEEDBACK")
                isEmbeddedMakeOfferTestDisabled: isEnabled(key: "disablePdpEmbeddedMakeOfferTest")
                ...PdpRespDealerDetailsStats_viewer
                ...ContactDealerInlineForm_viewer
                ...PdpSharedSellerTabsVariant_viewer
            }
        `,
        user: graphql`
            fragment PdpRespDealerDetails_user on User {
                isVerifiedTrade
                ...PdpRespDealerDetailsStats_user
                ...ContactDealerInlineForm_user
                ...PdpSharedSellerTabsVariant_user
                ...PdpSharedCallDealerCta_user
            }
        `,
        item: graphql`
            fragment PdpRespDealerDetails_item on Item {
                ...PdpSharedCallDealerCta_item
                ...ContactDealerInlineForm_item
                ...PdpSharedSellerTabsVariant_item
                dealerDetailsDisplayInfo: displayInfo {
                    ctas {
                        showMakeOffer
                    }
                }
                seller {
                    serviceId
                    trackedPhone {
                        phoneNumber
                    }
                    orderFeedbackPdp {
                        ...PdpRespDealerDetailsStats_reviews
                        ...PdpRespDealerDetailsFeedback_reviews
                    }
                    ...PdpRespDealerDetailsFeedback_seller
                    ...PdpRespDealerDetailsStats_seller
                }
            }
        `,
    }
);
